// @ts-nocheck
import { capitalize, compact } from 'lodash';
import { useMemo, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { normalize, normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { getCampaignStatus, REPORTING_COLORS } from '@/helpers/reporting.helpers';
import { getBaseCurrency } from '@/store/selectors/baseCurrency.selectors';
import {
  getCampaignItemDetailsCampaignStatus,
  getCampaignItemDetailsEventFamilies,
  getCampaignItemDetailsSearchText,
  getCampaignItemDetailsTrendPeriod,
  getSelectedCampaigns,
  isCampaignItemDetailsAnalyticalViewEnabled,
  isCampaignItemDetailsOnlyTopCampaignsEnabled
} from '@/store/selectors/reporting.selectors';
import { getCampaigns, getCurrency } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { useSummaryContext } from '@/ts-contexts/reporting/Summary/SummaryContext';

import type { ItemLineDetailProps } from 'sonata-react-components/lib/types/src_ts/components/ItemLineDetails/ItemLineDetails';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { GoalInterface } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';
import type { SummaryItemKey } from '@/ts-contexts/reporting/Summary/SummaryContext';

const CHART_CONFIG = {
  areaConfig: {
    fillColor: 'rgba(188,202,247,0.8)',
    fillOpacity: 1
  },
  height: 30,
  legend: false,
  lineOptions: {
    lineColor: '#676E8A',
    lineWidth: 1
  },
  plotMarkersEnabled: false,
  series: [],
  tooltipHoverEnabled: false,
  type: 'area',
  xAxisConfig: {
    visible: false
  },
  yAxisConfigs: [
    {
      visible: false
    }
  ]
};

function getLineGoalData(
  formatMessage: IntlShape['formatMessage'],
  overGoal: GoalInterface,
  aggregatedStats: Record<string, any>,
  currencyInfo: BaseCurrencyInterface
) {
  const { target: targetGoal, type: goalType } = overGoal;
  const currentGoal = aggregatedStats[goalType] ?? 0;
  const currentStatus = ((currentGoal ?? 0) / targetGoal) * 100;
  const formattedValue = currentStatus >= 100 ? 100 : normalize(currentStatus, 2, currencyInfo.countries![0]);
  return {
    goalDisplayNumber: `${formattedValue}%`,
    goalName: formatMessage({
      defaultMessage: capitalize(goalType),
      id: `reporting.goals.${goalType}`
    }),
    goalTarget: targetGoal,
    goalValue: currentGoal > targetGoal ? targetGoal : currentGoal
  };
}

function formatItemLineValueNumber(
  value: number,
  isCurrency: boolean,
  decimalDigits = 0,
  currencyInfo: BaseCurrencyInterface
) {
  return isCurrency
    ? normalizeWithUnit(value, currencyInfo, decimalDigits)
    : normalize(value, decimalDigits, currencyInfo.countries![0]);
}

function formatItemLineData(
  itemKey: SummaryItemKey,
  overviewItemData: Record<string, any>,
  currencyInfo: BaseCurrencyInterface,
  campaignDetailStats: Record<string, any>[],
  formatMessage: IntlShape['formatMessage']
) {
  const {
    familyItemApplies,
    main: {
      decimalDigits: mainItemValueDecimalDigits,
      isCurrency: mainItemValueIsCurrency,
      isPercentage: mainItemValueIsPercentage,
      metricKey,
      name: mainItemName,
      value: mainItemValue
    },
    secondary: secondaryItems = []
  } = overviewItemData;
  const formattedValue = formatItemLineValueNumber(
    mainItemValue,
    mainItemValueIsCurrency,
    mainItemValueDecimalDigits,
    currencyInfo
  );
  return {
    bottomMetricLabels: (secondaryItems as Record<string, any>[]).map(secondaryItem => {
      const {
        decimalDigits: secondaryValueDecimalDigits = 0,
        isCurrency: secondaryValueIsCurrency,
        isPercentage: secondaryValueIsPercentage,
        name: secondaryName,
        value: secondaryValue
      } = secondaryItem;
      const copy = formatMessage({
        defaultMessage: secondaryName.substring(secondaryName.lastIndexOf('.')),
        id: secondaryName
      });
      const formattedSecondaryValue = formatItemLineValueNumber(
        secondaryValue,
        secondaryValueIsCurrency,
        secondaryValueDecimalDigits,
        currencyInfo
      );
      return `${copy}: ${formattedSecondaryValue}${secondaryValueIsPercentage ? '%' : ''}`;
    }),
    chartConfig: {
      ...CHART_CONFIG,
      series: [
        {
          data: campaignDetailStats.map(stats => stats[metricKey] || 0).reverse(),
          name: itemKey
        }
      ]
    },
    id: itemKey,
    isChampion: false,
    isNA: !familyItemApplies,
    metricLabel: formatMessage({
      defaultMessage: mainItemName.substring(mainItemName.lastIndexOf('.')),
      id: mainItemName
    }),
    metricValue: `${formattedValue}${mainItemValueIsPercentage ? '%' : ''}`
  };
}

function markItemsLineDataChampions(
  selectedEventFamilies: SummaryItemKey[],
  topCampaignByEventFamily: Record<string, any>,
  itemsLinesData: (ItemLineDetailProps & { campaignId: string })[]
) {
  itemsLinesData.forEach(itemsLineData => {
    const { campaignId } = itemsLineData;
    selectedEventFamilies?.forEach((eventFamily, eventFamilyIndex) => {
      if (topCampaignByEventFamily[eventFamily].campaignId === campaignId) {
        itemsLineData.items[eventFamilyIndex].isChampion = true;
      }
    });
  });
}

type State = 'idle' | 'success' | 'empty';

export function useCampaignPerformance({ filterSection }: { filterSection: string }) {
  const { formatMessage } = useIntl();
  const [generatingItemsStatus, setGeneratingItemsStatus] = useState<State>('idle');
  const isOnlyTopCampaignsEnabled = useSelector(isCampaignItemDetailsOnlyTopCampaignsEnabled);
  const analyticalViewIsEnabled = useSelector(isCampaignItemDetailsAnalyticalViewEnabled);
  // @ts-ignore
  const currentCurrency = useSelector(store => getCurrency(store, filterSection));
  // @ts-ignore
  const currencyInfo: BaseCurrencyInterface = useSelector(store => getBaseCurrency(store, currentCurrency));
  const selectedEventFamilies: SummaryItemKey[] = useSelector(getCampaignItemDetailsEventFamilies);
  const selectedCampaignIds: string[] = useSelector(getCampaigns);
  const campaignsData: Record<string, any>[] = useSelector(getSelectedCampaigns);
  const selectedTrendPeriod = useSelector(getCampaignItemDetailsTrendPeriod);
  const selectedCampaignStatus = useSelector(getCampaignItemDetailsCampaignStatus);
  const currentSearchText = useSelector(getCampaignItemDetailsSearchText);

  const {
    getSummaryAnalyticalCampaignByDayStats,
    getSummaryDOOHStats,
    getSummaryDetailStats,
    getSummaryItem,
    getSummaryStatsByCampaign,
    summaryAnalyticalCampaignByDayStatsError,
    summaryAnalyticalCampaignByDayStatsReady,
    summaryDetailStatsError,
    summaryDetailStatsReFetch,
    summaryDetailStatsReady,
    summaryStatsError,
    summaryStatsReady
  } = useSummaryContext();
  const analyticalCampaignsByDayStats = getSummaryAnalyticalCampaignByDayStats();
  const statsByCampaign = getSummaryStatsByCampaign();
  const detailStats = getSummaryDetailStats();
  const doohStats = getSummaryDOOHStats();

  const statsAreReady = summaryStatsReady && summaryAnalyticalCampaignByDayStatsReady && summaryDetailStatsReady;
  const statsErrors = summaryStatsError || summaryAnalyticalCampaignByDayStatsError || summaryDetailStatsError;

  const itemsLinesData = useMemo(() => {
    if (statsAreReady) {
      const topCampaignByEventFamily: { [index in SummaryItemKey]?: Record<string, any> } = {};
      const filteredItemsLinesData = compact(
        selectedCampaignIds.map(campaignId => {
          const campaignData = campaignsData.find(campaignDataEntry => campaignDataEntry.id === campaignId);
          const { ioNumber, name, overallGoal } = campaignData!;
          const campaignName = `${ioNumber ? `${ioNumber} - ` : ''}${name}`;
          const campaignAggregatedStats = {
            ...(statsByCampaign[campaignId] || {}),
            screenAvgImpacts: doohStats.screenAvgImpactsByCampaign
              ? doohStats.screenAvgImpactsByCampaign[campaignId] || 0
              : 0,
            screensNumber: doohStats.screensNumberByCampaign ? doohStats.screensNumberByCampaign[campaignId] || 0 : 0
          };
          const campaignDetailStats = detailStats.find(({ id }) => id === campaignId);
          const analyticalCampaignByDayStats = analyticalCampaignsByDayStats[campaignId] || [];
          const campaignStatus = getCampaignStatus(campaignDetailStats);
          if (
            selectedCampaignStatus &&
            selectedCampaignStatus?.length &&
            !selectedCampaignStatus?.includes(campaignStatus)
          ) {
            return undefined;
          }
          if (currentSearchText.length && !campaignName.toLowerCase().includes(currentSearchText.toLowerCase())) {
            return undefined;
          }
          return {
            campaignId,
            items: selectedEventFamilies?.map(eventFamily => {
              const overviewItemData = getSummaryItem(eventFamily, [campaignData!], campaignAggregatedStats);
              const itemValue = overviewItemData.main.value ?? 0;
              if (!topCampaignByEventFamily[eventFamily] || itemValue > topCampaignByEventFamily[eventFamily]!.value) {
                topCampaignByEventFamily[eventFamily] = {
                  campaignId,
                  value: itemValue
                };
              }
              return formatItemLineData(
                eventFamily,
                overviewItemData,
                currencyInfo,
                // @ts-ignore
                analyticalViewIsEnabled ? analyticalCampaignByDayStats : [],
                formatMessage
              );
            }),
            lineGoal: overallGoal
              ? getLineGoalData(formatMessage, overallGoal, campaignAggregatedStats, currencyInfo)
              : undefined,
            lineName: campaignName,
            lineStatus: campaignStatus,
            progressBarColor: REPORTING_COLORS.impressionsKpisChart
          } as ItemLineDetailProps & { campaignId: string };
        })
      );
      markItemsLineDataChampions(selectedEventFamilies, topCampaignByEventFamily, filteredItemsLinesData);
      const items = filteredItemsLinesData.filter(filteredItemLinesData => {
        return !isOnlyTopCampaignsEnabled || filteredItemLinesData.items.some(itemData => itemData.isChampion);
      });
      setGeneratingItemsStatus('success');
      return items;
    }
  }, [
    analyticalCampaignsByDayStats,
    analyticalViewIsEnabled,
    campaignsData,
    detailStats,
    currentSearchText,
    isOnlyTopCampaignsEnabled,
    selectedCampaignStatus,
    selectedEventFamilies,
    selectedTrendPeriod,
    statsAreReady,
    statsByCampaign
  ]);

  return {
    columns: selectedEventFamilies || [],
    inAnalytical: analyticalViewIsEnabled,
    isError: statsErrors,
    isReady: statsAreReady && generatingItemsStatus === 'success',
    items: statsAreReady ? itemsLinesData : [],
    onReload: summaryDetailStatsReFetch
  };
}
